import classNames from 'classnames';
import { IconChevronDown, IconX } from 'featherico';
import { constant, defaultTo, filter, flow, identity, keyBy, map } from 'lodash/fp';
import { useEffect, useState } from 'react';
import useIdGen from '../../hooks/useIdGen';
import Button from '../Button';
import ClickOutside from '../ClickOutside';
import styles from './style.m.less';

export interface Option<A> {
    id: string;
    name: string;
    value: A;
    disabled?: boolean;
}

type Selected = Record<string, unknown>;

export interface CheckableOption<A> extends Option<A> {
    checked: boolean;
}

interface EffectfulOption<A> extends Option<A> {
    /**
     * Compute whether the checkbox is checked.
     * Eg. For "All" we'd check if all options are selected.
     */
    isChecked: (options: CheckableOption<A>[]) => boolean;

    /**
     * Given all options, give those selected.
     * Eg. select or deselect all. Or pick the top 10.
     */
    onSelect: (checked: boolean, options: CheckableOption<A>[]) => CheckableOption<A>[];
}

export const selectAllOption = (name: string): EffectfulOption<unknown> => ({
    id: '_selectAllOption',
    name,
    value: null,
    isChecked: options => options.every(x => x.checked),
    onSelect: (checked, options) => (checked ? options.map(o => ({ ...o, checked: true })) : []),
});

export const makeOption =
    <A extends any>(toId: (a: A) => string, toName: (a: A) => string) =>
    (value: A) => ({
        id: toId(value),
        name: toName(value),
        value,
    });

const makeCheckableOption =
    <A extends any>(selected: Selected) =>
    (option: Option<A>): CheckableOption<A> => ({
        ...option,
        checked: option.id in selected,
    });

type DropdownProps<A> = {
    options: CheckableOption<A>[];
    effectfulOptions: EffectfulOption<A>[];
    onChange: (selected: CheckableOption<A>[]) => void;
    selectMultiple?: boolean;
    role?: string;
    'aria-labelledby'?: string;
};

const Dropdown = <A extends any>({
    options,
    effectfulOptions,
    onChange,
    selectMultiple,
    role,
    'aria-labelledby': ariaLabelledby,
}: DropdownProps<A>) => {
    const handleCheckboxChange = (id: string) => (checked: boolean) => {
        onChange(
            options.map(o => (o.id === id ? { ...o, checked } : { ...o, checked: selectMultiple ? o.checked : false }))
        );
    };

    return (
        <div className={styles.dropdown} role={role} aria-labelledby={ariaLabelledby}>
            <ul className={styles.optionList}>
                {effectfulOptions.map(x => (
                    <li key={x.id} className={classNames(styles.optionListItem, styles.optionListItemGroupEnd)}>
                        <label className={styles.optionCheckboxLabel}>
                            <input
                                type="checkbox"
                                disabled={x.disabled}
                                className={styles.optionCheckboxInput}
                                checked={x.isChecked(options)}
                                onChange={e => onChange(x.onSelect(e.target.checked, options))}
                            />
                            {x.name}
                        </label>
                    </li>
                ))}

                {options.map(x => (
                    <li key={x.id} className={styles.optionListItem}>
                        <label className={styles.optionCheckboxLabel}>
                            <input
                                checked={x.checked}
                                type={selectMultiple ? 'checkbox' : 'radio'}
                                disabled={x.disabled}
                                className={styles.optionCheckboxInput}
                                onChange={e => handleCheckboxChange(x.id)(e.target.checked)}
                            />
                            {x.name}
                        </label>
                    </li>
                ))}
            </ul>
        </div>
    );
};

type Props<A> = {
    isOpen: boolean;
    options: Option<A>[];
    effectfulOptions?: EffectfulOption<A>[];
    summaryText?: (selected: CheckableOption<A>[]) => string;
    selected: string[];
    onChange: (selected: string[]) => void;
    full?: boolean;
    disabled?: boolean;
    highlighted?: boolean;
    hasResetButton?: boolean;
    selectMultiple?: boolean;
    isSmall?: boolean;
};

const CheckboxSelect = <A extends any>({
    summaryText,
    full,
    isOpen,
    options,
    selected,
    effectfulOptions,
    onChange,
    disabled = false,
    highlighted = false,
    hasResetButton,
    selectMultiple = true,
    isSmall,
}: Props<A>) => {
    const [filterSelectOpen, setFilterSelectOpen] = useState(isOpen);
    const id = useIdGen();

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setFilterSelectOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const selectedRecord = keyBy(identity, selected);
    const checkableOptions = options.map(makeCheckableOption(selectedRecord));
    const effectfulOptionsList = effectfulOptions ? effectfulOptions : [];
    const handleChange = (newSelected: CheckableOption<A>[]) =>
        flow(
            constant(newSelected),
            filter(x => x.checked),
            map(x => x.id),
            onChange
        )();

    const summaryTextOrDefault = defaultTo(() => 'Select', summaryText);
    const isEmpty = !checkableOptions.some(option => option.checked);

    return (
        <>
            <ClickOutside onClickOutside={() => setFilterSelectOpen(false)} enabled={filterSelectOpen}>
                <div className={styles.buttonContainer}>
                    <Button
                        className={classNames(styles.selectButton, {
                            [styles.selectButtonFull]: full,
                            [styles.highlighted]: highlighted,
                        })}
                        onClick={() => setFilterSelectOpen(!filterSelectOpen)}
                        disabled={disabled}
                        isSmall={isSmall}
                        aria-expanded={filterSelectOpen}
                        id={id('button')}
                    >
                        <span className={styles.selectButtonText}>{summaryTextOrDefault(checkableOptions)}</span>
                        {
                            <IconChevronDown
                                className={classNames(styles.selectButtonIcon, {
                                    [styles.selectButtonHidden]: !(!hasResetButton || isEmpty),
                                })}
                            />
                        }
                    </Button>
                    {!isEmpty && hasResetButton && (
                        <Button
                            kind="PLAIN"
                            className={styles.resetButton}
                            onClick={() => onChange([])}
                            aria-label="Reset"
                        >
                            <IconX />
                        </Button>
                    )}
                </div>

                {filterSelectOpen && !disabled && (
                    <Dropdown
                        options={checkableOptions}
                        effectfulOptions={effectfulOptionsList}
                        onChange={handleChange}
                        selectMultiple={selectMultiple}
                        role="region"
                        aria-labelledby={id('button')}
                    />
                )}
            </ClickOutside>
        </>
    );
};

export default CheckboxSelect;
